import { type QueryClient, queryOptions, useMutation } from '@tanstack/react-query'
import type { TemplateDocument, UploadTemplateAsset, TemplateTestSend } from '@headline/schemas'
import { api } from './api'

// ── The shared template library ───────────────────────────────────────────────────────────
//
// Its own key prefix and its own endpoints, mirroring the API: shared templates belong to no
// account, so nothing about them hangs under an account's cache key either.

export function sharedTemplatesQuery() {
  return queryOptions({
    queryKey: ['shared-report-templates'],
    queryFn: async () => {
      const res = await api.api['report-templates'].$get()
      if (!res.ok) {
        // This route's only failure is the middleware 403, which never reaches the handler, so
        // ErrorResponse isn't in its inferred union and `'error' in body` narrows to never.
        const body = (await res.json()) as { error?: { message?: string } }
        throw new Error(body.error?.message ?? 'Failed to load shared templates')
      }
      return res.json()
    },
  })
}

/** Every template across every account, with its owner — the internal Template Library's list.
 *  Staff-only at the API (`templates:manage`). */
export function allTemplatesQuery() {
  return queryOptions({
    queryKey: ['all-report-templates'],
    queryFn: async () => {
      const res = await api.api['report-templates'].all.$get()
      if (!res.ok) {
        const body = (await res.json()) as { error?: { message?: string } }
        throw new Error(body.error?.message ?? 'Failed to load templates')
      }
      return res.json()
    },
  })
}

export function sharedTemplateQuery(templateId: string) {
  return queryOptions({
    queryKey: ['shared-report-templates', templateId],
    queryFn: async () => {
      const res = await api.api['report-templates'][':templateId'].$get({ param: { templateId } })
      if (!res.ok) {
        const body = await res.json()
        throw new Error('error' in body ? body.error.message : 'Failed to load the template')
      }
      return res.json()
    },
  })
}

/** Templates a report in this account may choose: its own, plus the shared library. */
export function selectableTemplatesQuery(accountId: string) {
  return queryOptions({
    queryKey: ['accounts', accountId, 'selectable-templates'],
    queryFn: async () => {
      const res = await api.api.accounts[':accountId']['selectable-templates'].$get({
        param: { accountId },
      })
      if (!res.ok) {
        const body = await res.json()
        throw new Error('error' in body ? body.error.message : 'Failed to load templates')
      }
      return res.json()
    },
  })
}

/**
 * Set a template's single owner — an account, or the shared library (`accountId: null`). The
 * Template Library's "Assign to…" action; generalizes the old shared→account move. Invalidates the
 * library list and the shared list; the destination account's own list too when it's an account.
 * (The SOURCE account's list isn't known here, but the Library page — this action's only caller —
 * reads `all-report-templates`, which is invalidated, so what's on screen refreshes.)
 */
export function useSetTemplateOwner(queryClient: QueryClient) {
  return useMutation({
    mutationFn: async (input: { templateId: string; accountId: string | null }) => {
      const res = await api.api['report-templates'][':templateId'].account.$patch({
        param: { templateId: input.templateId },
        json: { accountId: input.accountId },
      })
      if (!res.ok) {
        const payload = await res.json()
        throw new Error(
          'error' in payload ? payload.error.message : 'Failed to reassign the template',
        )
      }
      return res.json()
    },
    onSuccess: (_template, input) => {
      void queryClient.invalidateQueries({ queryKey: ['all-report-templates'] })
      // Exact: the detail key sits under this prefix, and the template is no longer shared, so a
      // refetch of it would 404. The page that shows it drops that entry after it navigates away.
      void queryClient.invalidateQueries({ queryKey: ['shared-report-templates'], exact: true })
      // The destination is the library (null) for an account→library move — no account list to touch.
      if (input.accountId) {
        void queryClient.invalidateQueries({
          queryKey: ['accounts', input.accountId, 'report-templates'],
        })
      }
      void queryClient.invalidateQueries({ queryKey: ['reports-overview'] })
    },
  })
}

/**
 * Uploading an image for a template's masthead (CLI-695). No cache to invalidate: the response is
 * the path the editor writes into the document, and the document is what gets saved.
 */
export function useUploadTemplateAsset() {
  // Nothing caches assets: the response is written into the document, which is what gets saved.
  // react-doctor-disable-next-line react-doctor/query-mutation-missing-invalidation
  return useMutation({
    mutationFn: async (input: UploadTemplateAsset) => {
      const res = await api.api['template-assets'].$post({ json: input })
      if (!res.ok) {
        const body = await res.json()
        throw new Error('error' in body ? body.error.message : 'Failed to upload the image')
      }
      return res.json()
    },
  })
}

/**
 * Copy a template into one or more scopes (each an accountId, or `null` for the library). Every
 * target gets its own independent copy; refreshes the Library list so the copies appear.
 */
export function useCopyTemplate(queryClient: QueryClient) {
  return useMutation({
    mutationFn: async (input: { templateId: string; targets: Array<string | null> }) => {
      const res = await api.api['report-templates'][':templateId'].copies.$post({
        param: { templateId: input.templateId },
        json: { targets: input.targets },
      })
      if (!res.ok) {
        const payload = await res.json()
        throw new Error('error' in payload ? payload.error.message : 'Failed to copy the template')
      }
      return res.json()
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['all-report-templates'] }),
  })
}

export function useCreateSharedTemplate(queryClient: QueryClient) {
  return useMutation({
    mutationFn: async (input: { name: string; document?: TemplateDocument }) => {
      const res = await api.api['report-templates'].$post({ json: input })
      if (!res.ok) {
        const payload = await res.json()
        throw new Error(
          'error' in payload ? payload.error.message : 'Failed to create the template',
        )
      }
      return res.json()
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['shared-report-templates'] }),
  })
}

/**
 * Turns the starter catalogue into real shared templates.
 *
 * One call, because "install the ones that are missing" is a fact the server can establish in a
 * single pass — four POSTs from here would race each other into the unique index and report a
 * conflict for work that had actually succeeded.
 */
export function useInstallStarterTemplates(queryClient: QueryClient) {
  return useMutation({
    mutationFn: async () => {
      const res = await api.api['report-templates'].starters.$post()
      // No typed error body on this one: the handler returns only 201, and its failure modes are
      // the permission gate and a 500, neither of which the client's types know about.
      if (!res.ok) throw new Error('Failed to add the starter templates')
      return res.json()
    },
    // Both lists: the Template Library reads the all-owners one, and its banner is what offers
    // the install — without this it kept nagging about rows that had just been created.
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['shared-report-templates'] })
      void queryClient.invalidateQueries({ queryKey: ['all-report-templates'] })
    },
  })
}

/**
 * The standard layout alone, as a library row: what makes the email every untemplated report
 * sends editable, without the other starters landing in the library beside it.
 */
export function useInstallStandardLayout(queryClient: QueryClient) {
  return useMutation({
    mutationFn: async () => {
      const res = await api.api['report-templates'].starters.standard.$post()
      if (!res.ok) throw new Error('Failed to install the standard layout')
      return res.json()
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['shared-report-templates'] })
      void queryClient.invalidateQueries({ queryKey: ['all-report-templates'] })
      // The report Template tab links to the row once it exists; its list is keyed per account.
      void queryClient.invalidateQueries({
        predicate: (query) => query.queryKey.includes('selectable-templates'),
      })
    },
  })
}

/**
 * A test send from the template editor (CLI-867): the edited document, dummy coverage in the shape
 * of the chosen report, to the addresses named. Nothing to invalidate — nothing is recorded.
 */
export function useSendTemplateTest() {
  return useMutation({
    mutationFn: async (input: TemplateTestSend) => {
      const res = await api.api['report-templates']['test-sends'].$post({ json: input })
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { error?: { message?: string } } | null
        throw new Error(body?.error?.message ?? 'The test send failed')
      }
      return res.json()
    },
  })
}

export function useUpdateSharedTemplate(queryClient: QueryClient) {
  return useMutation({
    mutationFn: async (input: {
      templateId: string
      name?: string
      document?: TemplateDocument
    }) => {
      const { templateId, ...body } = input
      const res = await api.api['report-templates'][':templateId'].$put({
        param: { templateId },
        json: body,
      })
      if (!res.ok) {
        const payload = await res.json()
        throw new Error('error' in payload ? payload.error.message : 'Failed to save the template')
      }
      return res.json()
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['shared-report-templates'] }),
  })
}

export function useDeleteSharedTemplate(queryClient: QueryClient) {
  return useMutation({
    mutationFn: async (input: { templateId: string }) => {
      const res = await api.api['report-templates'][':templateId'].$delete({
        param: { templateId: input.templateId },
      })
      if (!res.ok) {
        const payload = await res.json()
        throw new Error(
          'error' in payload ? payload.error.message : 'Failed to delete the template',
        )
      }
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['shared-report-templates'] }),
  })
}
